import { useId, type ReactNode } from "react";

/**
 * "Same as X" checkbox that collapses a secondary block of fields (mailing
 * address, owner address, withdrawal account). Checked = the block is hidden
 * and the source is reused on the wire; unchecked = the block renders below
 * under a small sub-heading.
 *
 * Presentation only -- copy/seed-on-uncheck behavior stays with the caller
 * (see the mailSame / ownerAddrSame effects in NewApplication).
 */
export function SameAsToggle({
  checked,
  onChange,
  label,
  subtitle,
  children,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Checkbox text, e.g. "Mailing address is the same as the business address". */
  label: string;
  /** Heading shown above the expanded block, e.g. "Mailing address". */
  subtitle: string;
  children: ReactNode;
  /** Locks the checkbox (e.g. the intake form, when the block it controls was
   *  pre-filled by the operator and is read-only). */
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="inline-flex cursor-pointer items-center gap-2.5 text-sm text-brand-text"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
      {!checked && (
        <div className="mt-4 space-y-4 rounded-lg border border-brand-border/80 bg-brand-bg/40 p-4">
          <p className="form-subsection-title">{subtitle}</p>
          {children}
        </div>
      )}
    </div>
  );
}
